import { Hono } from "hono";
import { z } from "zod";
import type { Library } from "../../core/library.js";
import { type ListOptions, listReferences } from "../../features/operations/list.js";
import { sortFieldSchema, sortOrderSchema } from "../../features/pagination/index.js";
import { pickDefined } from "../../utils/object.js";

/**
 * Request body schema for list endpoint
 */
const listRequestBodySchema = z.object({
  sort: sortFieldSchema.optional(),
  order: sortOrderSchema.optional(),
  limit: z.number().int().min(0).optional(),
  offset: z.number().int().min(0).optional(),
  includeSuperseded: z.boolean().optional(),
});

/**
 * Request body type for list endpoint
 */
export type ListRequestBody = z.infer<typeof listRequestBodySchema>;

/**
 * Creates list route for HTTP server
 */
export function createListRoute(library: Library) {
  const route = new Hono();

  // POST / - List all references
  route.post("/", async (c) => {
    // Parse request body
    let body: unknown;
    try {
      body = await c.req.json();
    } catch {
      return c.json({ error: "Invalid JSON" }, 400);
    }

    // Validate body with zod
    const parseResult = listRequestBodySchema.safeParse(body);
    if (!parseResult.success) {
      const errorMessage = parseResult.error.issues[0]?.message ?? "Invalid request body";
      return c.json({ error: errorMessage }, 400);
    }

    const requestBody = parseResult.data;

    // Build options for listReferences
    const options: ListOptions = pickDefined(requestBody, [
      "sort",
      "order",
      "limit",
      "offset",
    ] as const);

    // Call listReferences operation - always returns raw CslItem[]
    const result = await listReferences(library, options);

    return c.json(result);
  });

  return route;
}
